import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import useKothar from "context/useKothar";
import {
  fetchAdmissionById,
  fetchAdmissionClientsPage,
  fetchAdmissionsByClientId,
} from "../Admissions/admissionListApi";
import { normalizeAdmissionAttachments } from "../Admissions/AddAdmissions/normalizeAdmissionAttachments";
import EmailDocumentsDialog from "../Admissions/AddAdmissions/EmailDocumentsDialog";
import { fetchLinkedDocuments } from "../Clients/documents/clientDocuments.api";

const CLIENT_PAGE_SIZE = 20;

const dedupeByClientId = (rows) => {
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const key = row?.clientId ?? row?.id;
    if (key == null || seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
};

const ClientListbox = forwardRef(function ClientListbox(props, ref) {
  const { children, loadingMore, reachedEnd, ...listProps } = props;
  return (
    <ul {...listProps} ref={ref}>
      {children}
      {loadingMore && (
        <li className="flex items-center justify-center gap-2 py-2 text-sm text-app-text-muted">
          <CircularProgress size={14} /> Loading more…
        </li>
      )}
      {reachedEnd && <li className="py-2 text-center text-xs text-app-text-muted">End of list</li>}
    </ul>
  );
});

// A client can hold several admissions; label each one clearly enough to disambiguate.
const buildAdmissionLabel = (adm, courseList, uniData) => {
  const courseName = courseList?.find(
    (c) => Number(c.courseId) === Number(adm?.course)
  )?.courseName;
  const uniName = uniData?.find((u) => Number(u?.id) === Number(adm?.university))?.name;
  const parts = [courseName || "Course —", uniName, adm?.intake, adm?.status].filter(Boolean);
  return parts.join(" · ");
};

const SendAdmissionDocumentsDialog = ({ open, onClose }) => {
  const [{ uniData, courseList }, { refetchUniData, refetchCourseList }] = useKothar();

  const [clientSearch, setClientSearch] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [clientPage, setClientPage] = useState(0);
  const [clientHasMore, setClientHasMore] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false); // initial / replace load
  const [loadingMoreClients, setLoadingMoreClients] = useState(false); // append (scroll) load
  const [client, setClient] = useState(null);
  // Bumped on every new search / close so responses from superseded requests are ignored.
  const clientReqRef = useRef(0);

  const [admissions, setAdmissions] = useState([]);
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);
  const [admissionId, setAdmissionId] = useState("");

  const [resolving, setResolving] = useState(false);
  const [emailProps, setEmailProps] = useState(null);

  // University contact emails (for To prefill) live in uniData, which is lazy-loaded app-wide.
  useEffect(() => {
    if (open && !uniData?.length) refetchUniData?.();
    if (open && !courseList?.length) refetchCourseList?.();
  }, [open, uniData, refetchUniData, courseList, refetchCourseList]);

  useEffect(() => {
    if (open) return;
    clientReqRef.current += 1; // invalidate any in-flight client load
    setClientSearch("");
    setClientOptions([]);
    setClientPage(0);
    setClientHasMore(false);
    setLoadingClients(false);
    setLoadingMoreClients(false);
    setClient(null);
    setAdmissions([]);
    setLoadingAdmissions(false);
    setAdmissionId("");
    setResolving(false);
    setEmailProps(null);
  }, [open]);

  // One loader for both the first page (append=false, replaces) and scroll pages (append=true).
  const loadClients = useCallback(async (term, pageToLoad, append, reqId) => {
    if (append) setLoadingMoreClients(true);
    else setLoadingClients(true);
    try {
      const res = await fetchAdmissionClientsPage({
        page: pageToLoad,
        pageSize: CLIENT_PAGE_SIZE,
        search: term,
      });
      if (reqId !== clientReqRef.current) return; // a newer search superseded this one
      const content = Array.isArray(res?.content) ? res.content : [];
      const totalPages = Number(res?.totalPages) || 0;
      setClientOptions((prev) => (append ? dedupeByClientId([...prev, ...content]) : content));
      setClientPage(pageToLoad);
      // Prefer the server's totalPages; fall back to "a full page came back" when it's absent.
      setClientHasMore(
        totalPages ? pageToLoad + 1 < totalPages : content.length === CLIENT_PAGE_SIZE
      );
    } catch {
      if (reqId !== clientReqRef.current) return;
      if (!append) setClientOptions([]);
      setClientHasMore(false);
    } finally {
      if (reqId === clientReqRef.current) {
        if (append) setLoadingMoreClients(false);
        else setLoadingClients(false);
      }
    }
  }, []);

  // Debounced search — each fire bumps the request id, resetting to page 0.
  useEffect(() => {
    if (!open) return;
    const term = clientSearch.trim();
    const handle = setTimeout(() => {
      const reqId = (clientReqRef.current += 1);
      setClientHasMore(false);
      loadClients(term, 0, false, reqId);
    }, 300);
    return () => clearTimeout(handle);
  }, [open, clientSearch, loadClients]);

  useEffect(() => {
    if (!client?.clientId) {
      setAdmissions([]);
      setAdmissionId("");
      return;
    }
    let active = true;
    setLoadingAdmissions(true);
    setAdmissions([]);
    setAdmissionId("");
    fetchAdmissionsByClientId(client.clientId)
      .then((list) => {
        if (!active) return;
        const rows = Array.isArray(list) ? list : [];
        setAdmissions(rows);
        if (rows.length === 1) setAdmissionId(String(rows[0].id));
      })
      .catch(() => active && setAdmissions([]))
      .finally(() => active && setLoadingAdmissions(false));
    return () => {
      active = false;
    };
  }, [client]);

  const selectedAdmission = useMemo(
    () => admissions.find((a) => String(a.id) === String(admissionId)) || null,
    [admissions, admissionId]
  );

  const handleContinue = async () => {
    if (!selectedAdmission) {
      toast.error("Select an admission.");
      return;
    }
    setResolving(true);
    try {
      const value = await fetchAdmissionById(selectedAdmission.id);
      const documents = normalizeAdmissionAttachments(value || {});
      // A document shown on this admission only via "Show in module" never lands in its own
      // attachments -- EmailDocumentsDialog merges those in itself, so only bail here when there
      // is truly nothing to offer on either side.
      const linkedDocuments = await fetchLinkedDocuments("ADMISSION", selectedAdmission.id).catch(
        () => []
      );
      if (documents.length === 0 && linkedDocuments.length === 0) {
        toast.info("This admission has no saved documents yet.");
        onClose();
        return;
      }
      const universityId = Number(value?.university) || null;
      const university = universityId
        ? uniData?.find((u) => Number(u?.id) === universityId) ?? null
        : null;
      setEmailProps({
        studentId: selectedAdmission.id,
        documents,
        universityId: universityId ?? undefined,
        universityName: university?.name,
        universityEmail: university?.email,
        clientName: value?.name,
        clientStudentId: value?.studentId,
        clientEmail: value?.email,
        clientId: client?.clientId,
        clientPhone: client?.number || client?.phone || value?.number || value?.phone,
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load admission documents.");
    } finally {
      setResolving(false);
    }
  };

  const handleEmailClose = () => {
    setEmailProps(null);
    onClose();
  };

  // Load the next page once the user scrolls near the bottom — but never past the end.
  const handleClientScroll = (event) => {
    if (loadingClients || loadingMoreClients || !clientHasMore) return;
    const node = event.currentTarget;
    if (node.scrollTop + node.clientHeight >= node.scrollHeight - 48) {
      loadClients(clientSearch.trim(), clientPage + 1, true, clientReqRef.current);
    }
  };

  // Only announce the end once real pagination has happened (not for a tiny single-page result).
  const clientsReachedEnd =
    clientOptions.length >= CLIENT_PAGE_SIZE && !clientHasMore && !loadingMoreClients;

  const pickerOpen = open && !emailProps;

  return (
    <>
      <Dialog open={pickerOpen} onClose={resolving ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle className="font-semibold text-app-text">Send admission documents</DialogTitle>
        <DialogContent className="space-y-4 pt-1">
          <Typography variant="body2" className="text-app-text-muted">
            Pick a client and admission — their saved documents are prepared for emailing, exactly
            like the Email documents action inside an admission.
          </Typography>

          <Autocomplete
            options={clientOptions}
            loading={loadingClients}
            value={client}
            onChange={(_, next) => setClient(next)}
            onInputChange={(_, next, reason) => {
              if (reason !== "reset") setClientSearch(next);
            }}
            filterOptions={(x) => x}
            isOptionEqualToValue={(o, v) => o?.clientId === v?.clientId}
            getOptionLabel={(o) => (o?.name ? `${o.name}${o.email ? ` · ${o.email}` : ""}` : "")}
            noOptionsText={clientSearch.trim() ? "No matching clients" : "Type to search clients"}
            ListboxComponent={ClientListbox}
            ListboxProps={{
              onScroll: handleClientScroll,
              loadingMore: loadingMoreClients,
              reachedEnd: clientsReachedEnd,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client"
                placeholder="Search by name, email, passport…"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingClients ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {client && (
            <div>
              {loadingAdmissions ? (
                <div className="flex items-center gap-2 text-app-text-muted">
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading admissions…</Typography>
                </div>
              ) : admissions.length === 0 ? (
                <Alert severity="warning">
                  This client has no admissions yet. Create an admission before emailing documents.
                </Alert>
              ) : admissions.length === 1 ? (
                <Alert severity="info">
                  Admission: <b>{buildAdmissionLabel(admissions[0], courseList, uniData)}</b>
                </Alert>
              ) : (
                <FormControl>
                  <FormLabel className="text-sm text-app-text-secondary">
                    Which admission?
                  </FormLabel>
                  <RadioGroup value={admissionId} onChange={(e) => setAdmissionId(e.target.value)}>
                    {admissions.map((adm) => (
                      <FormControlLabel
                        key={adm.id}
                        value={String(adm.id)}
                        control={<Radio size="small" />}
                        label={buildAdmissionLabel(adm, courseList, uniData)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions className="px-4 pb-3">
          <Button onClick={onClose} color="inherit" disabled={resolving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleContinue}
            disabled={resolving || !selectedAdmission}
            startIcon={resolving ? <CircularProgress size={16} /> : null}
          >
            {resolving ? "Loading…" : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>

      <EmailDocumentsDialog
        open={open && !!emailProps}
        onClose={handleEmailClose}
        {...(emailProps || {})}
      />
    </>
  );
};

export default SendAdmissionDocumentsDialog;
